import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ClickAwayListener,
  DeleteIcon,
  Divider,
  Down1Icon,
  Drag1Icon,
  Edit1Icon,
  GKButton,
  Grid2,
  IconButton,
  KebabMenu2Icon,
  Plus1Icon,
  PopConfirm,
  Popover,
  Show,
  Typography,
} from "components";
import { map } from "lodash";
import { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useDeleteFaqEntryMutation } from "store/features/MyWebsite";
import FaqModal from "./FaqModal";
import { Faq, WebsiteDetails } from "./types";

const FaqsForm = () => {
  const form = useFormContext<WebsiteDetails>();

  const [openFaqModal, setOpenFaqModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Faq | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<
    boolean | null
  >(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const [deleteFaqEntry] = useDeleteFaqEntryMutation();

  const { fields, move } = useFieldArray({
    control: form.control,
    name: "websiteSection.faqs",
  });

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={12}>
        <GKButton
          startIcon={<Plus1Icon />}
          onClick={() => {
            setSelectedRowData(null);
            setOpenFaqModal(true);
          }}
        >
          Add New Question
        </GKButton>
      </Grid2>

      <Grid2 size={12}>
        {map(fields, (row, index) => (
          <Accordion
            key={row.id}
            draggable
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragIndex === null || dragIndex === index) return;
              move(dragIndex, index);
              setDragIndex(null);
            }}
            onDragEnd={() => setDragIndex(null)}
            onDragEnter={(e) => e.preventDefault()}
            onDragLeave={(e) => e.preventDefault()}
            expanded={expandedAccordion === index}
            onChange={() => {
              setExpandedAccordion(expandedAccordion !== index ? index : null);
            }}
            elevation={0}
            sx={{
              borderBottom: "1px solid var(--neutral-100)",
              "&::before": { display: "none" },
              opacity: dragIndex === index ? 0.5 : 1,
              cursor: "grab",
            }}
          >
            <AccordionSummary>
              <Grid2 size={12} container spacing={2}>
                <Grid2 size={10} display={"flex"} alignItems={"center"}>
                  <IconButton sx={{ cursor: "move" }}>
                    <Drag1Icon color={"var(--neutral-500)"} />
                  </IconButton>

                  <Controller
                    name={`websiteSection.faqs.${index}.question`}
                    control={form.control}
                    render={({ field }) => (
                      <Typography color="var(--black)" fontWeight={500}>
                        {field.value}
                      </Typography>
                    )}
                  />
                </Grid2>
                <Grid2
                  size={2}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                >
                  <IconButton>
                    <Down1Icon
                      color={
                        expandedAccordion === index
                          ? "var(--neutral-500)"
                          : "var(--black)"
                      }
                      style={{
                        transition: "all 0.3s",
                        transform:
                          expandedAccordion === index ? "rotate(180deg)" : "",
                      }}
                    />
                  </IconButton>
                  <Divider orientation="vertical" sx={{ height: 16 }} />
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRowData(row);
                      setAnchorEl(e.currentTarget);
                    }}
                  >
                    <KebabMenu2Icon color="var(--black)" />
                  </IconButton>
                </Grid2>
              </Grid2>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Controller
                    name={`websiteSection.faqs.${index}.answer`}
                    control={form.control}
                    render={({ field }) => (
                      <Typography
                        color="var(--neutral-700)"
                        fontSize={14}
                        fontWeight={400}
                        dangerouslySetInnerHTML={{ __html: field.value }}
                      />
                    )}
                  />
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid2>

      {Boolean(anchorEl) && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Popover
            id={popoverId}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  width: "90px",
                  borderRadius: "8px",
                  p: "8px 0",
                  border: "1px solid var(--neutral-100)",
                  bgcolor: "var(--white)",
                  boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
                },
              },
            }}
          >
            <Grid2 container>
              <Grid2
                size={12}
                p={"12px"}
                display={"flex"}
                alignItems={"center"}
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setOpenFaqModal(true);
                }}
              >
                <Edit1Icon />
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="var(--grey-900)"
                >
                  Edit
                </Typography>
              </Grid2>
              <Grid2
                size={12}
                p={"12px"}
                display={"flex"}
                alignItems={"center"}
                gap={1}
                color={"var(--warning-700)"}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setOpenDeleteConfirmation(true);
                }}
              >
                <DeleteIcon />
                <Typography fontSize={12} fontWeight={600}>
                  Delete
                </Typography>
              </Grid2>
            </Grid2>
          </Popover>
        </ClickAwayListener>
      )}

      <Show.When isTrue={openDeleteConfirmation}>
        <PopConfirm
          anchorEl={null}
          open={openDeleteConfirmation}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          setOpen={setOpenDeleteConfirmation}
          onSuccess={async () => {
            try {
              await deleteFaqEntry(selectedRowData?.rowId).unwrap();
            } catch (error) {
              console.error("Delete faq entry failed:", error);
            }
          }}
        />
      </Show.When>

      <Show.When isTrue={!!openFaqModal}>
        <FaqModal
          open={openFaqModal}
          setOpen={setOpenFaqModal}
          selectedRowData={selectedRowData}
        />
      </Show.When>
    </Grid2>
  );
};

export default FaqsForm;
